import React, { useMemo } from "react";
import { Stack, Group, Text, Paper, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getTravelTypeConfig } from "@/config";
import { Travel } from "@/utils/api/TravelService";

import { useDashboardContext } from "@/contexts/DashboardContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";

import { useElapsedTime } from "@/hooks/useElapsedTime";

import { AppIcon } from "@/components/generic/AppIcon";

import classes from "./TripSummaryCard.module.css";

// ============================================
// Types
// ============================================

export interface TripSummaryCardProps {
  /** The active travel to display */
  activeTravel: Travel | null;
}

// ============================================
// Pulsing Dot
// ============================================

const PulsingDot: React.FC<{ size?: number }> = ({ size = 10 }) => (
  <Box
    className={classes.pulsingDot}
    style={{
      width: size,
      height: size,
    }}
  />
);

// ============================================
// Component
// ============================================

const TripSummaryCardComponent: React.FC<TripSummaryCardProps> = ({
  activeTravel,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort } = useUserProfileContext();
  const { getCarById } = useDashboardContext();

  // Elapsed time — always ticking while the card is mounted
  const elapsedTime = useElapsedTime(activeTravel?.firstDttm);

  // ============================================
  // Derived values
  // ============================================

  const selectedCar = useMemo(() => {
    if (!activeTravel?.carId) return null;
    return getCarById(activeTravel.carId);
  }, [activeTravel?.carId, getCarById]);

  const odometerUnit = useMemo(
    () => selectedCar?.mileageIn || distanceLabelShort,
    [selectedCar?.mileageIn, distanceLabelShort]
  );

  const unitLabel = t(`common:units.distance.${odometerUnit}Short`);

  const travelTypeConfig = useMemo(
    () => getTravelTypeConfig(activeTravel?.travelType),
    [activeTravel?.travelType]
  );

  const startOdometer = useMemo(
    () =>
      activeTravel?.firstOdometer ??
      activeTravel?.firstRecord?.odometer ??
      null,
    [activeTravel]
  );

  const startingLocation = useMemo(
    () =>
      activeTravel?.firstRecord?.whereDone ||
      activeTravel?.firstRecord?.location ||
      null,
    [activeTravel]
  );

  const traveledDistance = useMemo(() => {
    const start =
      activeTravel?.firstOdometer ??
      activeTravel?.firstRecord?.odometer ??
      0;

    const waypoints = activeTravel?.waypoints;
    const latestWaypoint =
      Array.isArray(waypoints) && waypoints.length > 0
        ? waypoints[waypoints.length - 1]
        : null;

    const last = latestWaypoint?.odometer ?? latestWaypoint?.tripMeter ?? 0;

    if (start != null && last != null && last > start) {
      return last - start;
    }
    return null;
  }, [activeTravel]);

  // ============================================
  // Render
  // ============================================

  if (!activeTravel) return null;

  return (
    <Paper
      p="sm"
      radius="md"
      bg="neutral.0"
      style={{ border: "1px solid var(--mantine-color-neutral-2)" }}
    >
      <Stack gap={6}>
        {/* Row 1: Status + Elapsed Time */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <PulsingDot size={8} />
            <Text size="sm" fw={600} c="green.7">
              {t("travels:banner.tripInProgress")}
            </Text>
          </Group>
          <Paper
            px="xs"
            py={2}
            radius="sm"
            bg="white"
            style={{ border: "1px solid var(--mantine-color-neutral-2)" }}
          >
            <Group gap={4} wrap="nowrap">
              <AppIcon
                iconName="Clock"
                size={14}
                color="var(--mantine-color-neutral-5)"
              />
              <Text size="sm" fw={700} c="neutral.8" ff="monospace">
                {elapsedTime}
              </Text>
            </Group>
          </Paper>
        </Group>

        {/* Row 2: Vehicle + Starting Odometer + Distance Traveled */}
        <Group gap="xs" wrap="nowrap">
          <AppIcon
            iconName="Car"
            size={16}
            color="var(--mantine-color-brand-6)"
            style={{ flexShrink: 0 }}
          />
          <Text size="sm" fw={500} c="neutral.8" lineClamp={1} style={{ minWidth: 0 }}>
            {activeTravel.car?.label || t("travels:labels.unknownVehicle")}
          </Text>

          {startOdometer != null && (
            <>
              <Text c="neutral.4" style={{ flexShrink: 0 }}>•</Text>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                <AppIcon
                  iconName="Gauge"
                  size={14}
                  color="var(--mantine-color-neutral-5)"
                />
                <Text size="xs" c="neutral.6">
                  {startOdometer.toLocaleString()} {unitLabel}
                </Text>
              </Group>
            </>
          )}

          {traveledDistance != null && (
            <>
              <Text c="neutral.4" style={{ flexShrink: 0 }}>•</Text>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                <AppIcon
                  iconName="Route"
                  size={14}
                  color="var(--mantine-color-green-6)"
                />
                <Text size="xs" fw={600} c="green.7">
                  {traveledDistance.toLocaleString()}{" "}
                  {t(`common:${distanceLabelShort}`)}
                </Text>
              </Group>
            </>
          )}
        </Group>

        {/* Row 3: Trip Type */}
        {travelTypeConfig && (
          <Group gap={4} wrap="nowrap">
            <AppIcon
              iconName={travelTypeConfig.icon}
              size={14}
              color={`var(--mantine-color-${travelTypeConfig.color}-6)`}
              style={{ flexShrink: 0 }}
            />
            <Text size="sm" c="neutral.6">
              {t(travelTypeConfig.translationKey)}
            </Text>
          </Group>
        )}

        {/* Row 4: Starting Location (own line — can be long) */}
        {startingLocation && (
          <Group gap={4} wrap="nowrap">
            <AppIcon
              iconName="MapPinFilled"
              size={14}
              color="var(--mantine-color-green-5)"
              style={{ flexShrink: 0 }}
            />
            <Text size="xs" c="neutral.6" lineClamp={1} style={{ minWidth: 0 }}>
              {startingLocation}
            </Text>
          </Group>
        )}

        {/* Row 5: Purpose (own line — can be long) */}
        {activeTravel.purpose && (
          <Group gap={4} wrap="nowrap">
            <AppIcon
              iconName="FileText"
              size={14}
              color="var(--mantine-color-neutral-4)"
              style={{ flexShrink: 0 }}
            />
            <Text size="xs" c="neutral.5" lineClamp={1} style={{ minWidth: 0 }}>
              {activeTravel.purpose}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

export const TripSummaryCard = React.memo(TripSummaryCardComponent);